
import React from 'react';
import { Toggle } from 'rsuite';

import 'rsuite/Toggle/styles/index.css';
import { inflectNumber } from '../../utils';
import { getWeekOfMonth, startOfMonth } from '../calendar/utils/dates';
import { CronData, mergeCronData } from './utils';
import { Updater } from 'use-immer';

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const WEEK_NAMES = ["1st", "2nd", "3rd", "4th"];

export function nthDayOfNthWeek(date: Date): string {
  return `${inflectNumber(1 + getWeekOfMonth(startOfMonth(date), date))} ${DAY_NAMES[date.getDay()].slice(0, 3)}`
}

export function nthDayOfMonth(date: Date): string {
  return inflectNumber(date.getDate())
}

function updateWith(
  setCronData: Updater<CronData>
) {
  return (
    val: Partial<CronData>
  ) => {
    return (checked: boolean) => {
      const update = checked ? { add: val } : { sub: val };
      setCronData((prev) => mergeCronData(prev, update));
    }
  }
}
type UpdateWith = ReturnType<typeof updateWith>;

function setTo(
  setCronData: Updater<CronData>
) {
  return (
    checkedValue: Partial<CronData>,
    unCheckedValue?: Partial<CronData>
  ) => {
    return checked => checked ? 
      setCronData((prev) => ({ ...prev, ...checkedValue })) : 
      setCronData((prev) => ({ ...prev, ...unCheckedValue }));
  }
}
type SetTo = ReturnType<typeof setTo>;

export const EditDaysOfWeek: React.FC<{
  cronData: CronData;
  updateWith: UpdateWith,
  setTo: SetTo
}> = ({
  cronData,
  updateWith,
  setTo
}) => {

  return <div className="flex flex-col space-y-2">
    <Toggle 
      color="green"
      checked={cronData.daysOfWeek.length === 0}
      onChange={setTo({ daysOfWeek: [] })}
    >
      Every day
    </Toggle>

    { DAY_NAMES.map((day, index) => (
      <Toggle 
        key={day}
        data-testid={day}
        color="green"
        checked={cronData.daysOfWeek.includes(index)}
        onChange={updateWith({ daysOfWeek: [index] })}
      >
        {day}
      </Toggle>
    ))}
  </div>
}

export const EditWeeksOfMonth: React.FC<{
  cronData: CronData;
  updateWith: UpdateWith,
  setTo: SetTo
}> = ({
  cronData,
  updateWith,
  setTo
}) => {
  return <div className="flex flex-col space-y-2">
    <Toggle 
      color="green"
      checked={cronData.weeksOfMonth.length === 0}
      onChange={setTo({ weeksOfMonth: [] })}
    >
      Every week
    </Toggle>

    { WEEK_NAMES.map((week, index) => (
      <Toggle 
        key={week} 
        color="green" 
        checked={cronData.weeksOfMonth.includes(index + 1)}
        onChange={updateWith({ weeksOfMonth: [index + 1] })}
      >
        {week} week of month
      </Toggle>
    ))}
  </div>
}

export const EditDaysOfMonth: React.FC<{
  cronData: CronData;
  updateWith: UpdateWith,
  setTo: SetTo
}> = ({
  cronData,
  updateWith,
  setTo
}) => {
  const date = cronData.date;
  return <div>
    <div className="flex flex-row space-x-2">
      <Toggle 
        color="green"
        unCheckedChildren={nthDayOfNthWeek(date)}
        checkedChildren={nthDayOfMonth(date)}
        checked={cronData.daysOfMonth.length !== 0}
        onChange={setTo(
          { daysOfMonth: [date.getDate()] }, 
          { daysOfMonth: [], 
            daysOfWeek: [date.getDay()], 
            weeksOfMonth: [1 + getWeekOfMonth(startOfMonth(date), date)] }
        )}
      />
      <p>of</p>
      <Toggle 
        color="green"
        checked={cronData.months.length === 0}
        checkedChildren="every month"
        unCheckedChildren="specific months"
        onChange={setTo({ months: [] })}
      />
    </div>
    <br />
    <div className={`grid grid-cols-2 space-y-2 space-x-2`}>
      { MONTH_NAMES.map((month, index) => {
        return <Toggle 
          key={month} 
          color="green"
          checked={cronData.months.includes(index)}
          onChange={updateWith({ months: [index] })}
        >
          {month}
        </Toggle>
      })}
    </div>
  </div>
}

export const CronEditor: React.FC<{
  cronData: CronData;
  setCronData: Updater<CronData>;
}> = ({
  cronData,
  setCronData,
}) => {
  const cronType = cronData.cronType;

  return <div className="flex flex-col">
    { cronType === "daily" && <EditDaysOfWeek cronData={cronData} updateWith={updateWith(setCronData)} setTo={setTo(setCronData)} /> }
    { cronType === "weekly" && <EditWeeksOfMonth cronData={cronData} updateWith={updateWith(setCronData)} setTo={setTo(setCronData)} /> }
    { cronType === "monthly" && <EditDaysOfMonth cronData={cronData} updateWith={updateWith(setCronData)} setTo={setTo(setCronData)} /> }
  </div>
}
